<div class="relative bg-white rounded-lg shadow dark:bg-gray-800 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
  <!-- Modal header -->
  <div class="flex items-center justify-between p-5 border-b rounded-t dark:border-gray-600 bg-gradient-to-r from-primary-600 to-primary-700">
    <h3 class="text-xl font-semibold text-white">
      {{ (isAnonymous ? 'Ask for Anonymous Feedback' : 'Request Feedback') | translate }}
    </h3>
    <button type="button" 
            (click)="close()"
            class="text-white bg-transparent hover:bg-primary-800 hover:text-white rounded-lg text-sm w-8 h-8 ml-auto inline-flex justify-center items-center">
      <svg class="w-3 h-3" fill="none" viewBox="0 0 14 14">
        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/>
      </svg>
    </button>
  </div>

  <!-- Modal body -->
  <div class="p-6 space-y-6">
    <!-- Anonymous Toggle -->
    <div class="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
      <div class="flex-1">
        <label class="text-sm font-medium text-gray-900 dark:text-white">
          {{ 'Anonymous Feedback' | translate }}
        </label>
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {{ isAnonymous ? 'People won\'t know who gave feedback' : 'You\'ll know who gave you feedback' | translate }}
        </p>
      </div>
      <button type="button"
              (click)="isAnonymous = !isAnonymous"
              [class.bg-primary-600]="isAnonymous"
              [class.bg-gray-200]="!isAnonymous"
              class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2">
        <span [class.translate-x-5]="isAnonymous"
              [class.translate-x-0]="!isAnonymous"
              class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"></span>
      </button>
    </div>

    <!-- Info Banner -->
    <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
      <div class="flex">
        <svg class="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path>
        </svg>
        <div class="text-sm text-blue-800 dark:text-blue-200">
          <p class="font-semibold">{{ isAnonymous ? 'Anonymous Feedback' : 'Named Feedback' | translate }}</p>
          <p class="mt-1">{{ isAnonymous ? 'Create a custom evaluation and either send email invitations or generate a shareable link.' : 'People you invite will need to create an account or sign in to give you feedback.' | translate }}</p>
        </div>
      </div>
    </div>

    <!-- Step 1: Title & Description -->
    <div class="space-y-4">
      <h4 class="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
        <span class="flex items-center justify-center w-7 h-7 bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 rounded-full mr-2 text-sm font-bold">1</span>
        {{ 'What is this feedback about?' | translate }}
      </h4>
      
      <!-- Title -->
      <div>
        <label class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
          {{ 'Feedback Title' | translate }} <span class="text-red-500">*</span>
        </label>
        <input type="text"
               [(ngModel)]="evaluationTitle"
               [disabled]="loading"
               placeholder="e.g., 'My Leadership Skills' or 'Project Management Feedback'"
               class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white">
        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {{ 'Give your feedback request a clear, specific title' | translate }}
        </p>
      </div>

      <!-- Description (Optional) -->
      <div>
        <label class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
          {{ 'Description' | translate }} <span class="text-gray-400 text-xs">({{ 'Optional' | translate }})</span>
        </label>
        <textarea [(ngModel)]="evaluationDescription"
                  rows="2"
                  [disabled]="loading"
                  placeholder="Add context about what you're working on or what you'd like feedback on..."
                  class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"></textarea>
      </div>

      <!-- Get Suggestions Button -->
      <button (click)="getSuggestedSkills()"
              type="button"
              [disabled]="!evaluationTitle.trim() || loadingSuggestions"
              class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed">
        <svg *ngIf="loadingSuggestions" class="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <svg *ngIf="!loadingSuggestions" class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
        </svg>
        {{ loadingSuggestions ? ('Analyzing...' | translate) : ('Get AI Suggestions' | translate) }}
      </button>
    </div>

    <!-- Step 2: Skills/Indicators Selection -->
    <div class="space-y-4">
      <h4 class="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
        <span class="flex items-center justify-center w-7 h-7 bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 rounded-full mr-2 text-sm font-bold">2</span>
        {{ 'Select Skills to Evaluate' | translate }}
      </h4>

      <!-- Skills Lock Toggle -->
      <div class="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
        <div class="flex items-center space-x-3">
          <svg *ngIf="skillsLocked" class="w-5 h-5 text-gray-600 dark:text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd"></path>
          </svg>
          <svg *ngIf="!skillsLocked" class="w-5 h-5 text-gray-600 dark:text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 2a5 5 0 00-5 5v2a2 2 0 00-2 2v5a2 2 0 002 2h10a2 2 0 002-2v-5a2 2 0 00-2-2H7V7a3 3 0 015.905-.75 1 1 0 001.937-.5A5.002 5.002 0 0010 2z"></path>
          </svg>
          <div>
            <p class="text-sm font-medium text-gray-900 dark:text-white">
              {{ skillsLocked ? ('Skills Locked' | translate) : ('Skills Unlocked' | translate) }}
            </p>
            <p class="text-xs text-gray-500 dark:text-gray-400">
              {{ skillsLocked ? ('Recipients cannot modify selected skills' | translate) : ('Recipients can add or remove skills' | translate) }}
            </p>
          </div>
        </div>
        <label class="relative inline-flex items-center cursor-pointer">
          <input type="checkbox" 
                 [(ngModel)]="skillsLocked"
                 class="sr-only peer">
          <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
        </label>
      </div>

      <!-- Skills Tree Select -->
      <div>
        <app-tree-select
          [items]="allIndicators"
          [selectedItems]="selectedIndicatorIds"
          (selectionChange)="onIndicatorSelectionChange($event)">
        </app-tree-select>
        <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
          <span class="font-medium text-primary-600 dark:text-primary-400">{{ selectedIndicatorIds.length }}</span> {{ selectedIndicatorIds.length === 1 ? ('skill selected' | translate) : ('skills selected' | translate) }}
          <span *ngIf="selectedIndicatorIds.length === 0 && skillsLocked" class="text-red-500"> - {{ 'Please select at least one skill' | translate }}</span>
          <span *ngIf="selectedIndicatorIds.length === 0 && !skillsLocked" class="text-gray-400"> - {{ 'Recipients can choose skills' | translate }}</span>
        </p>
      </div>
    </div>

    <!-- Step 3: Distribution Method -->
    <div class="space-y-4">
      <h4 class="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
        <span class="flex items-center justify-center w-7 h-7 bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 rounded-full mr-2 text-sm font-bold">3</span>
        {{ 'How would you like to share this?' | translate }}
      </h4>

      <!-- Distribution Method Toggle -->
      <div class="flex space-x-2 p-1 bg-gray-100 dark:bg-gray-700 rounded-lg">
        <button (click)="distributionMethod = 'email'"
                type="button"
                [class.bg-white]="distributionMethod === 'email'"
                [class.shadow-sm]="distributionMethod === 'email'"
                [class.text-primary-600]="distributionMethod === 'email'"
                [class.text-gray-600]="distributionMethod !== 'email'"
                class="flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all">
          <svg class="w-4 h-4 inline mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"></path>
            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"></path>
          </svg>
          {{ 'Email Invitations' | translate }}
        </button>
        <button (click)="distributionMethod = 'link'"
                type="button"
                [class.bg-white]="distributionMethod === 'link'"
                [class.shadow-sm]="distributionMethod === 'link'"
                [class.text-primary-600]="distributionMethod === 'link'"
                [class.text-gray-600]="distributionMethod !== 'link'"
                class="flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all">
          <svg class="w-4 h-4 inline mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clip-rule="evenodd"></path>
          </svg>
          {{ 'Shareable Link' | translate }}
        </button>
      </div>

      <!-- Email Input (Chip-based) -->
      <div *ngIf="distributionMethod === 'email'">
        <label class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
          {{ 'Email Addresses' | translate }}
        </label>
        
        <!-- Email Chips Container -->
        <div class="bg-gray-50 border border-gray-300 rounded-lg p-2 min-h-[42px] flex flex-wrap gap-2 items-center focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500 dark:bg-gray-700 dark:border-gray-600">
          <!-- Existing email chips -->
          <div *ngFor="let contact of emailContacts; let i = index"
               class="inline-flex items-center px-2 py-1 rounded-md text-sm font-medium transition-all"
               [class.bg-green-100]="contact.isRegistered"
               [class.text-green-800]="contact.isRegistered"
               [class.bg-gray-200]="!contact.isRegistered"
               [class.text-gray-800]="!contact.isRegistered"
               [title]="contact.email">
            <svg *ngIf="contact.isRegistered" class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"></path>
            </svg>
            <span>{{ contact.name || contact.email }}</span>
            <button (click)="removeEmailContact(i)"
                    type="button"
                    class="ml-1 hover:text-red-600">
              <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
              </svg>
            </button>
          </div>
          
          <!-- Email input field -->
          <input type="text"
                 [(ngModel)]="emailInputValue"
                 (keydown)="onEmailInputKeydown($event)"
                 (paste)="onEmailInputPaste($event)"
                 [disabled]="loading"
                 placeholder="Type or paste emails (comma, space, or Enter to add)"
                 class="flex-1 min-w-[200px] bg-transparent border-none outline-none text-sm text-gray-900 dark:text-white placeholder-gray-400">
        </div>
        
        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {{ 'Paste multiple emails separated by commas, spaces, or press Enter to add each one. Registered users show with their name.' | translate }}
        </p>
      </div>

      <!-- Link Options -->
      <div *ngIf="distributionMethod === 'link'" class="space-y-3">
        <!-- One-time use toggle -->
        <div class="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <div class="flex items-center space-x-3">
            <svg class="w-5 h-5 text-gray-600 dark:text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd"></path>
            </svg>
            <div>
              <p class="text-sm font-medium text-gray-900 dark:text-white">
                {{ 'One-time use link' | translate }}
              </p>
              <p class="text-xs text-gray-500 dark:text-gray-400">
                {{ 'Link can only be used once for a single evaluation' | translate }}
              </p>
            </div>
          </div>
          <label class="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" 
                   [(ngModel)]="isOneTimeUse"
                   class="sr-only peer">
            <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
          </label>
        </div>

        <!-- Generate Link Button -->
        <button *ngIf="!generatedLink"
                (click)="generateLink()"
                type="button"
                [disabled]="!canGenerateLink()"
                class="w-full inline-flex justify-center items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed">
          <svg *ngIf="loading" class="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {{ loading ? ('Generating...' | translate) : ('Generate Link' | translate) }}
        </button>

        <!-- Generated Link Display -->
        <div *ngIf="generatedLink" class="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <p class="text-xs text-green-800 dark:text-green-200 font-medium mb-2">{{ 'Link generated successfully!' | translate }}</p>
          <div class="flex items-center space-x-2">
            <input type="text"
                   [value]="generatedLink"
                   readonly
                   class="flex-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-white rounded px-2 py-1">
            <button (click)="copyLinkToClipboard()"
                    type="button"
                    class="px-3 py-1 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded">
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z"></path>
                <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Optional Settings -->
    <div class="space-y-4 pt-4 border-t dark:border-gray-600">
      <!-- Personal Message (Optional) -->
      <div>
        <label class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
          {{ 'Personal Message' | translate }} <span class="text-gray-400 text-xs">({{ 'Optional' | translate }})</span>
        </label>
        <textarea [(ngModel)]="personalMessage"
                  rows="2"
                  [disabled]="loading"
                  placeholder="Add a personal note..."
                  class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"></textarea>
      </div>

      <!-- Expiration Days -->
      <div>
        <label class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
          {{ 'Invitation Expires In' | translate }}
        </label>
        <select [(ngModel)]="expireDays"
                [disabled]="loading"
                class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white">
          <option [value]="7">7 {{ 'days' | translate }}</option>
          <option [value]="14">14 {{ 'days' | translate }}</option>
          <option [value]="30">30 {{ 'days' | translate }}</option>
          <option [value]="60">60 {{ 'days' | translate }}</option>
          <option [value]="90">90 {{ 'days' | translate }}</option>
        </select>
      </div>
    </div>

    <!-- Summary -->
    <div *ngIf="getValidEmailContacts().length > 0 && selectedIndicatorIds.length > 0 && distributionMethod === 'email'" 
         class="bg-gradient-to-r from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 rounded-lg p-4 border border-primary-200 dark:border-primary-700">
      <p class="text-sm text-gray-700 dark:text-gray-300">
        <svg class="w-5 h-5 inline mr-2 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path>
        </svg>
        <span class="font-semibold">{{ 'Ready to send!' | translate }}</span><br>
        <span class="ml-7">{{ getValidEmailContacts().length }} {{ getValidEmailContacts().length === 1 ? ('person' | translate) : ('people' | translate) }} will evaluate {{ selectedIndicatorIds.length }} {{ selectedIndicatorIds.length === 1 ? ('skill' | translate) : ('skills' | translate) }}</span>
      </p>
    </div>
  </div>

  <!-- Modal footer -->
  <div class="flex items-center justify-end p-6 space-x-2 border-t border-gray-200 rounded-b dark:border-gray-600 bg-gray-50 dark:bg-gray-800">
    <button (click)="close()"
            type="button"
            [disabled]="loading"
            class="text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-gray-200 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-500 dark:hover:text-white dark:hover:bg-gray-600 dark:focus:ring-gray-600 disabled:opacity-50 disabled:cursor-not-allowed">
      {{ 'Cancel' | translate }}
    </button>
    <button *ngIf="distributionMethod === 'email'"
            (click)="sendInvitations()"
            type="button"
            [disabled]="!canSend()"
            class="text-white bg-primary-700 hover:bg-primary-800 focus:ring-4 focus:outline-none focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center">
      <svg *ngIf="loading" class="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      {{ loading ? ('Sending...' | translate) : ('Send Invitations' | translate) }}
    </button>
    <button *ngIf="distributionMethod === 'link' && generatedLink"
            (click)="close()"
            type="button"
            class="text-white bg-primary-700 hover:bg-primary-800 focus:ring-4 focus:outline-none focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800">
      {{ 'Done' | translate }}
    </button>
  </div>
</div>
